import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, X, Image, Layers, ChevronRight, Film, Loader2 } from 'lucide-react';
import { API, authFetch } from '@/App';
import { toast } from 'sonner';

const THEME_COLORS = {
  "Summer": "from-yellow-300 to-orange-400",
  "Wedding": "from-rose-400 to-pink-500",
  "Corporate": "from-slate-400 to-gray-600",
  "Everyday": "from-amber-300 to-orange-400",
  "Night Out": "from-indigo-500 to-purple-600",
  "Festival / Cultural": "from-purple-400 to-pink-500",
  "Glam / Editorial": "from-amber-400 to-red-500",
  "Seasonal": "from-green-300 to-emerald-500",
  "Skincare Journey": "from-teal-300 to-cyan-500",
  "Before & After": "from-violet-400 to-purple-600",
};

export default function CreateLookPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: theme, 1.5: sub-theme, 2: photos, 3: details
  const [themes, setThemes] = useState({});
  const [theme, setTheme] = useState('');
  const [subTheme, setSubTheme] = useState('');
  const [images, setImages] = useState([]);
  const [beforeImage, setBeforeImage] = useState(null);
  const [afterImage, setAfterImage] = useState(null);
  const [caption, setCaption] = useState('');
  const [checklists, setChecklists] = useState([]);
  const [selectedChecklist, setSelectedChecklist] = useState('');
  const [loading, setLoading] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState('');
  const [videoUploading, setVideoUploading] = useState(false);
  const MAX_VIDEO_DURATION = 20;

  useEffect(() => {
    fetchChecklists();
    fetchThemes();
  }, []);

  const fetchChecklists = async () => {
    try {
      const response = await authFetch(`${API}/checklists`);
      if (response.ok) setChecklists(await response.json());
    } catch (e) { console.error(e); }
  };

  const fetchThemes = async () => {
    try {
      const response = await authFetch(`${API}/looks/themes`);
      if (response.ok) setThemes(await response.json());
    } catch (e) { console.error(e); }
  };

  const isBeforeAfter = theme === 'Before & After';
  const currentStep = step === 1.5 ? 1 : step; // For display purposes

  const handleThemeSelect = (t) => {
    setTheme(t);
    setSubTheme('');
    const subs = themes[t] || [];
    if (subs.length > 0) {
      setStep(1.5); // Show sub-theme picker
    } else {
      setStep(2); // Skip to photos
    }
  };

  const handleSubThemeSelect = (st) => {
    setSubTheme(st);
    setStep(2);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setImages(prev => [...prev, ...newImages].slice(0, 5));
  };

  const handleBeforeAfterUpload = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const data = { file, preview: URL.createObjectURL(file) };
    if (type === 'before') setBeforeImage(data);
    else setAfterImage(data);
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Video must be under 50MB');
      return;
    }
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      if (video.duration > MAX_VIDEO_DURATION) {
        toast.error(`Video must be ${MAX_VIDEO_DURATION} seconds or less (yours: ${Math.round(video.duration)}s)`);
        return;
      }
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
    };
    video.src = URL.createObjectURL(file);
  };

  const removeVideo = () => {
    if (videoPreview) URL.revokeObjectURL(videoPreview);
    setVideoFile(null);
    setVideoPreview('');
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await authFetch(`${API}/users/upload-image`, {
      method: 'POST',
      body: formData
    });
    if (response.ok) {
      const data = await response.json();
      return data.url;
    }
    return null;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      let uploadedImages = [];
      let uploadedBefore = '';
      let uploadedAfter = '';
      let uploadedVideoUrl = '';

      // Upload video if present
      if (videoFile) {
        setVideoUploading(true);
        const videoFormData = new FormData();
        videoFormData.append('file', videoFile);
        const videoRes = await authFetch(`${API}/upload-media`, {
          method: 'POST',
          body: videoFormData,
        });
        if (!videoRes.ok) {
          const err = await videoRes.json();
          throw new Error(err.detail || 'Video upload failed');
        }
        const videoData = await videoRes.json();
        uploadedVideoUrl = videoData.url;
        setVideoUploading(false);
      }

      if (isBeforeAfter) {
        if (!beforeImage || !afterImage) {
          toast.error('Please add both before and after photos');
          setLoading(false);
          return;
        }
        uploadedBefore = await uploadImage(beforeImage.file) || '';
        uploadedAfter = await uploadImage(afterImage.file) || '';
      } else {
        if (images.length === 0 && !videoFile) {
          toast.error('Please add at least one photo or video');
          setLoading(false);
          return;
        }
        for (const img of images) {
          const url = await uploadImage(img.file);
          if (url) uploadedImages.push(url);
        }
      }

      const response = await authFetch(`${API}/looks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          theme,
          sub_theme: subTheme,
          images: uploadedImages,
          video_url: uploadedVideoUrl,
          before_image: uploadedBefore,
          after_image: uploadedAfter,
          caption,
          checklist_id: selectedChecklist
        })
      });

      if (response.ok) {
        toast.success('Look posted!');
        navigate('/spotlight');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to create look');
      }
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
    } finally {
      setLoading(false);
      setVideoUploading(false);
    }
  };

  const handleBack = () => {
    if (step === 1.5) setStep(1);
    else if (step === 2) {
      const subs = themes[theme] || [];
      setStep(subs.length > 0 ? 1.5 : 1);
    }
    else if (step === 3) setStep(2);
    else navigate(-1);
  };

  return (
    <div className="min-h-screen pb-24 bg-background">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={handleBack} className="p-2 hover:bg-primary/10 rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {step === 1 ? 'Choose Theme' : step === 1.5 ? 'Choose Style' : step === 2 ? 'Add Media' : 'Details'}
            </h1>
          </div>
          <span className="text-sm text-muted-foreground">Step {step === 1.5 ? '1' : currentStep}/3</span>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Step 1: Choose Parent Theme */}
        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">What kind of look are you sharing?</p>
            <div className="grid grid-cols-2 gap-3">
              {Object.keys(themes).map(t => (
                <button
                  key={t}
                  data-testid={`theme-${t.replace(/\s+/g, '-').toLowerCase()}`}
                  onClick={() => handleThemeSelect(t)}
                  className={`p-4 rounded-2xl border-2 transition-all text-left ${
                    theme === t
                      ? 'border-primary bg-primary/5 shadow-md'
                      : 'border-border hover:border-primary/40 hover:shadow-sm'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${THEME_COLORS[t] || 'from-gray-300 to-gray-500'} mb-3`} />
                  <p className="font-semibold text-sm">{t}</p>
                  {(themes[t]?.length > 0) && (
                    <p className="text-xs text-muted-foreground mt-1">{themes[t].length} styles</p>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1.5: Choose Sub-Theme */}
        {step === 1.5 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${THEME_COLORS[theme] || 'from-gray-300 to-gray-500'}`} />
              <div>
                <p className="font-bold text-sm">{theme}</p>
                <p className="text-xs text-muted-foreground">Pick a specific style</p>
              </div>
            </div>

            {/* General option */}
            <button
              data-testid="subtheme-general"
              onClick={() => { setSubTheme(''); setStep(2); }}
              className="w-full p-4 rounded-2xl border-2 border-border hover:border-primary/40 transition-all text-left flex items-center justify-between"
            >
              <span className="font-medium text-sm">General {theme}</span>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>

            {/* Sub-themes */}
            <div className="grid grid-cols-2 gap-3">
              {(themes[theme] || []).map(st => (
                <button
                  key={st}
                  data-testid={`subtheme-${st.replace(/\s+/g, '-').toLowerCase()}`}
                  onClick={() => handleSubThemeSelect(st)}
                  className={`p-4 rounded-2xl border-2 transition-all text-left ${
                    subTheme === st
                      ? 'border-primary bg-primary/5 shadow-md'
                      : 'border-border hover:border-primary/40 hover:shadow-sm'
                  }`}
                >
                  <p className="font-semibold text-sm">{st}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Add Photos */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full">
                {theme}
              </span>
              {subTheme && (
                <span className="inline-block px-3 py-1 bg-accent/10 text-accent text-sm font-medium rounded-full">
                  {subTheme}
                </span>
              )}
            </div>

            {isBeforeAfter ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">Upload your before and after photos</p>
                <div className="grid grid-cols-2 gap-4">
                  {/* Before */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2 uppercase">Before</p>
                    {beforeImage ? (
                      <div className="relative aspect-square rounded-xl overflow-hidden">
                        <img src={beforeImage.preview} alt="Before" className="w-full h-full object-cover" />
                        <button onClick={() => setBeforeImage(null)} className="absolute top-2 right-2 p-1 bg-black/60 text-white rounded-full">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="aspect-square rounded-xl border-2 border-dashed border-border hover:border-primary/50 flex flex-col items-center justify-center cursor-pointer transition-colors">
                        <Upload className="w-6 h-6 text-muted-foreground mb-1" />
                        <span className="text-xs text-muted-foreground">Before</span>
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleBeforeAfterUpload(e, 'before')} />
                      </label>
                    )}
                  </div>
                  {/* After */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2 uppercase">After</p>
                    {afterImage ? (
                      <div className="relative aspect-square rounded-xl overflow-hidden">
                        <img src={afterImage.preview} alt="After" className="w-full h-full object-cover" />
                        <button onClick={() => setAfterImage(null)} className="absolute top-2 right-2 p-1 bg-black/60 text-white rounded-full">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="aspect-square rounded-xl border-2 border-dashed border-border hover:border-primary/50 flex flex-col items-center justify-center cursor-pointer transition-colors">
                        <Upload className="w-6 h-6 text-muted-foreground mb-1" />
                        <span className="text-xs text-muted-foreground">After</span>
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleBeforeAfterUpload(e, 'after')} />
                      </label>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">Add up to 5 photos of your look</p>
                <div className="grid grid-cols-3 gap-3">
                  {images.map((img, i) => (
                    <div key={i} className="relative aspect-square rounded-xl overflow-hidden">
                      <img src={img.preview} alt={`Look ${i+1}`} className="w-full h-full object-cover" />
                      <button onClick={() => removeImage(i)} className="absolute top-1 right-1 p-1 bg-black/60 text-white rounded-full">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {images.length < 5 && (
                    <label className="aspect-square rounded-xl border-2 border-dashed border-border hover:border-primary/50 flex flex-col items-center justify-center cursor-pointer transition-colors">
                      <Upload className="w-6 h-6 text-muted-foreground mb-1" />
                      <span className="text-xs text-muted-foreground">{images.length}/5</span>
                      <input type="file" accept="image/*" multiple className="hidden" onChange={handleImageUpload} />
                    </label>
                  )}
                </div>
              </div>
            )}

            {/* Video upload */}
            <div className="space-y-3">
              <p className="text-sm font-medium">Video <span className="text-muted-foreground font-normal">(optional, max {MAX_VIDEO_DURATION}s)</span></p>
              {videoFile ? (
                <div className="relative rounded-xl overflow-hidden border border-border bg-black">
                  <video src={videoPreview} className="w-full max-h-40 object-contain" controls muted />
                  <button type="button" onClick={removeVideo} className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-full shadow-lg" data-testid="remove-look-video">
                    <X className="w-4 h-4" />
                  </button>
                  <div className="absolute top-2 left-2 px-2 py-1 bg-black/70 text-white text-xs rounded-full flex items-center gap-1">
                    <Film className="w-3 h-3" /> Video
                  </div>
                </div>
              ) : (
                <label className="flex items-center justify-center w-full h-16 border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-primary transition-colors bg-white/50" data-testid="look-video-upload-label">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Film className="w-4 h-4" />
                    <span className="text-sm">Add video (max {MAX_VIDEO_DURATION}s)</span>
                  </div>
                  <input type="file" accept="video/mp4,video/quicktime,video/webm,video/x-m4v" onChange={handleVideoUpload} className="hidden" data-testid="look-video-input" />
                </label>
              )}
            </div>

            <button
              data-testid="photos-next-btn"
              onClick={() => setStep(3)}
              disabled={isBeforeAfter ? (!beforeImage || !afterImage) : (images.length === 0 && !videoFile)}
              className="w-full btn-primary py-3 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}

        {/* Step 3: Details */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full">
                {theme}
              </span>
              {subTheme && (
                <span className="inline-block px-3 py-1 bg-accent/10 text-accent text-sm font-medium rounded-full">
                  {subTheme}
                </span>
              )}
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Caption</label>
              <textarea
                data-testid="look-caption"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Describe your look..."
                className="w-full p-3 rounded-xl bg-white border border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary transition-colors resize-none"
                rows={3}
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Attach Checklist *</label>
              <select
                data-testid="look-checklist-select"
                value={selectedChecklist}
                onChange={(e) => setSelectedChecklist(e.target.value)}
                className="w-full p-3 rounded-xl bg-white border border-border text-foreground focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
              >
                <option value="">Select a checklist</option>
                {checklists.map(c => (
                  <option key={c.checklist_id} value={c.checklist_id}>{c.name}</option>
                ))}
              </select>
              {checklists.length === 0 && (
                <p className="text-xs text-amber-600 mt-2">You need to create a checklist first before posting a Look.</p>
              )}
            </div>

            <button
              data-testid="submit-look-btn"
              onClick={handleSubmit}
              disabled={loading || !selectedChecklist}
              className="w-full btn-primary py-3 flex items-center justify-center gap-2 disabled:opacity-40"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {videoUploading ? 'Uploading video...' : 'Posting...'}
                </>
              ) : (
                <>
                  <Layers className="w-5 h-5" />
                  Post Look
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
